/*
  PRODUCT PAGE GALLERY (2026-09-15) - the photo switcher for
  sections/product-template.liquid.

  WHY THIS FILE EXISTS. Switching photos on a phone took seconds and landed
  with a jerk. The theme's own switch (theme.js `_switchImage`) is a class
  toggle and nothing else - it reveals the next wrapper and hides the current
  one, then leaves the picture to lazysizes. Measured on a throttled phone
  (Fast 3G, 390x844, dpr 3, 2026-09-15): the tap flipped the class at 322 ms,
  lazysizes noticed through its MutationObserver and wrote a srcset at 350 ms,
  and the 1080px JPEG it then asked for only finished at 3754 ms. For those
  3.4 seconds the box showed the image's own `src` - a 200x200 placeholder
  stretched over 1080 device pixels - so the photo looked unchanged and then
  snapped. Three taps 190 ms apart started three separate 1080px downloads and
  the one the shopper wanted landed last, behind the two they had abandoned.

  WHAT THIS DOES INSTEAD.
    - owns the switch: it intercepts the thumbnail click in the capture phase
      so theme.js cannot reveal a photo that has not decoded yet
    - loads on demand at a sane size: the markup carries the candidates in
      data-bc-srcset and this file writes `sizes` from the box it measured,
      capped at 2x (see DENSITY_CAP)
    - waits for img.decode() before revealing, so the photo arrives whole
      instead of painting in bands
    - shows the held aspect box and a skeleton while it waits - never the
      previous photo, which is what read as "nothing happened, then a snap"
    - keeps the neighbours (index +/-1) decoded ahead of time, so the common
      next/previous tap is instant, then tops up the rest one at a time while
      the link is idle
    - coalesces rapid taps: a photo that is still downloading and is no longer
      wanted (nor a neighbour of what is wanted) has its request dropped, so a
      fast sequence spends bandwidth on the last tap only
    - keeps the active thumbnail marked (the theme marked none at all) and
      follows variant switching, which calls theme.js `_switchImage` directly
      and never goes through a thumbnail click

  WHAT IT DELIBERATELY LEAVES ALONE. theme.js still owns the lightbox and the
  zoom-in plugin, both of which key off `.product-single__photos[data-image-id]`
  and `.gallery__item[data-image-id]`; the `hidden` class stays the single
  record of which photo is shown, because theme.getCartFlyImageSrc reads
  `.product-single__featured-image-wrapper:not(.hidden) img` for the
  add-to-cart animation.
*/
(function () {
  'use strict';

  /* A 3x screen gets a 2x file. Measured on this product's photos: the 750w
     is 57K against 113K for the 1080w, and at a 360px box the difference is
     not visible on a phone. */
  var DENSITY_CAP = 2;

  /* How far either side of the shown photo is kept ready at all times. */
  var NEIGHBOURS = 1;

  var SELECTORS = {
    root: '[data-bc-product-gallery]',
    wrapper: '.product-single__featured-image-wrapper',
    box: '.product-single__photos',
    photo: '.product-single__photo',
    thumbList: '#ProductThumbs',
    thumb: '.product-single__thumbnail'
  };

  var CLASSES = {
    hidden: 'hidden',
    js: 'bc-pgal--js',
    loading: 'bc-pgal--loading',
    ready: 'bc-pgal__img--ready',
    activeThumb: 'bc-pgal__thumb--active'
  };

  function supported() {
    return !!(window.Promise && window.MutationObserver && document.documentElement.classList);
  }

  /* Without the APIs above nothing below can run, and the photos that wait for
     this file would never load at all - so they are handed back to the browser
     exactly as the theme used to serve them. */
  function loadEverythingEagerly(root) {
    var photos = root.querySelectorAll('[data-bc-deferred]');
    for (var i = 0; i < photos.length; i++) {
      reveal(photos[i]);
    }
  }

  /* Hand a deferred photo to the browser: `sizes` first so the candidate is
     picked against it, then `src` as well as `srcset` so theme.getCartFlyImageSrc
     has a URL to read even mid-download (the browser still fetches one file). */
  function reveal(img, sizes) {
    if (sizes) img.setAttribute('sizes', sizes);
    else if (img.getAttribute('data-bc-sizes')) img.setAttribute('sizes', img.getAttribute('data-bc-sizes'));
    if (img.getAttribute('data-bc-srcset')) img.setAttribute('srcset', img.getAttribute('data-bc-srcset'));
    if (img.getAttribute('data-bc-src')) img.setAttribute('src', img.getAttribute('data-bc-src'));
  }

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function whenIdle(fn) {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(fn, { timeout: 1500 });
    } else {
      window.setTimeout(fn, 150);
    }
  }

  /* How much of the gallery to keep ahead of the shopper. A metered or very
     slow connection gets the neighbours only; everything else eventually gets
     the lot, one photo at a time so a tap is never stuck behind a preload. */
  function lookAhead() {
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return Infinity;
    if (conn.saveData) return 0;
    if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') return 0;
    if (conn.effectiveType === '3g') return NEIGHBOURS + 1;
    return Infinity;
  }

  function Gallery(root) {
    this.root = root;
    this.wrappers = toArray(root.querySelectorAll(SELECTORS.wrapper));
    if (!this.wrappers.length) return;

    this.photos = this.wrappers.map(this.describe, this);
    this.thumbs = this.collectThumbs();
    this.wanted = this.indexOfShown();
    if (this.wanted < 0) this.wanted = 0;

    root.classList.add(CLASSES.js);
    document.documentElement.classList.add('bc-pgal-ready');

    this.prime();
    this.syncThumbs();
    this.bind();
    this.topUpSoon();
  }

  Gallery.prototype.describe = function (wrapper, index) {
    return {
      index: index,
      wrapper: wrapper,
      box: wrapper.querySelector(SELECTORS.box),
      img: wrapper.querySelector(SELECTORS.photo),
      state: 'idle',
      generation: 0,
      promise: null
    };
  };

  Gallery.prototype.collectThumbs = function () {
    var list = document.querySelector(SELECTORS.thumbList);
    if (!list) return [];
    var self = this;
    return toArray(list.querySelectorAll(SELECTORS.thumb)).map(function (link) {
      var id = link.getAttribute('data-image-id');
      var index = -1;
      self.wrappers.forEach(function (wrapper, i) {
        if (wrapper.getAttribute('data-image-id') === id) index = i;
      });
      return { link: link, index: index };
    });
  };

  Gallery.prototype.indexOfShown = function () {
    for (var i = 0; i < this.wrappers.length; i++) {
      if (!this.wrappers[i].classList.contains(CLASSES.hidden)) return i;
    }
    return -1;
  };

  /* The featured photo is served with a real srcset in the markup so the
     preload scanner can start it before any script runs; it is only adopted
     here, never re-requested. */
  Gallery.prototype.prime = function () {
    var self = this;
    this.photos.forEach(function (photo) {
      if (!photo.img) return;
      if (!photo.img.getAttribute('srcset') && !photo.img.getAttribute('src')) return;
      if (photo.img.complete && photo.img.naturalWidth) {
        photo.state = 'ready';
        photo.img.classList.add(CLASSES.ready);
      } else {
        self.load(photo);
      }
    });
  };

  /* One box width serves the whole gallery: every photo sits in the same box,
     so the hidden ones (width 0) borrow the measurement from the shown one. */
  Gallery.prototype.boxWidth = function () {
    var width = 0;
    for (var i = 0; i < this.photos.length && !width; i++) {
      if (this.photos[i].box) width = this.photos[i].box.clientWidth;
    }
    return width || Math.round(document.documentElement.clientWidth * 0.92);
  };

  /* `sizes` is in CSS pixels and the browser multiplies it by the device
     ratio, so asking for a narrower box is how the density cap is expressed:
     a 360px box on a 3x screen asks for 240px and is served the 720w file. */
  Gallery.prototype.sizesAttr = function () {
    var ratio = window.devicePixelRatio || 1;
    var width = this.boxWidth() * Math.min(ratio, DENSITY_CAP) / ratio;
    return Math.max(120, Math.round(width)) + 'px';
  };

  Gallery.prototype.load = function (photo, priority) {
    if (!photo.img) return Promise.resolve();
    if (photo.promise) return photo.promise;

    var img = photo.img;
    var generation = ++photo.generation;

    if (img.hasAttribute('data-bc-deferred') && !img.getAttribute('src')) {
      if (priority) img.setAttribute('fetchpriority', priority);
      reveal(img, this.sizesAttr());
    }

    photo.state = 'loading';
    photo.promise = this.decoded(img).then(function () {
      if (photo.generation !== generation) return;
      photo.state = 'ready';
      img.classList.add(CLASSES.ready);
    }, function () {
      if (photo.generation !== generation) return;
      /* A photo that will not load is shown anyway rather than left under an
         endless skeleton - the alt text is then what the shopper gets. */
      photo.state = 'failed';
      img.classList.add(CLASSES.ready);
    });
    return photo.promise;
  };

  /* decode() is what keeps a half-drawn photo off the screen: it resolves once
     the pixels are ready to paint, so the reveal is one clean step. */
  Gallery.prototype.decoded = function (img) {
    return new Promise(function (resolve, reject) {
      function settle() {
        if (typeof img.decode === 'function') {
          img.decode().then(resolve, resolve);
        } else {
          resolve();
        }
      }
      if (img.complete && img.naturalWidth) {
        settle();
        return;
      }
      img.addEventListener('load', settle);
      img.addEventListener('error', reject);
    });
  };

  /* Drop a download the shopper has tapped past. Only photos this file started
     are ever cancelled - the featured one carries its srcset from the markup
     and has no data-bc-srcset to restore it from. */
  Gallery.prototype.cancel = function (photo) {
    if (photo.state !== 'loading') return;
    if (!photo.img || !photo.img.hasAttribute('data-bc-deferred')) return;
    photo.generation++;
    photo.promise = null;
    photo.state = 'idle';
    photo.img.classList.remove(CLASSES.ready);
    photo.img.removeAttribute('srcset');
    photo.img.removeAttribute('src');
  };

  /* While the shopper is waiting on a photo, nothing else may hold the wire -
     not even a neighbour, which would otherwise finish first and push the
     wanted one behind it (measured: three taps 180ms apart used to land the
     wanted photo last). Once it has arrived the usual reach applies again and
     the neighbours are picked back up by topUp. */
  Gallery.prototype.trim = function () {
    var self = this;
    var waiting = this.photos[this.wanted] && this.photos[this.wanted].state !== 'ready';
    this.photos.forEach(function (photo) {
      if (photo.index === self.wanted) return;
      if (waiting || Math.abs(photo.index - self.wanted) > NEIGHBOURS) self.cancel(photo);
    });
  };

  Gallery.prototype.show = function (index) {
    if (index < 0 || index >= this.photos.length) return;
    this.wanted = index;

    var self = this;
    this.photos.forEach(function (photo, i) {
      /* toggle() writes nothing when the class is already right, so this does
         not feed the observer below. */
      photo.wrapper.classList.toggle(CLASSES.hidden, i !== index);
    });

    this.syncThumbs();
    this.trim();

    var photo = this.photos[index];
    if (photo.state !== 'ready') this.preview(photo);
    this.paintState();
    if (photo.state !== 'ready') {
      this.load(photo, 'high').then(function () {
        self.paintState();
        self.topUpSoon();
      });
    } else {
      this.topUpSoon();
    }
  };

  /* The thumbnail the shopper just tapped is already decoded, so blurring it up
     into the box costs nothing and shows the photo they asked for rather than a
     blank rectangle. It is soft enough (240w over a 360px box) to read as a
     placeholder, which the theme's old 200x200 `src` was not - that one was
     sharp enough to be mistaken for the photo itself, which is why the switch
     looked like nothing had happened and then snapped. */
  Gallery.prototype.preview = function (photo) {
    var skeleton = photo.box && photo.box.querySelector('.bc-pgal__skeleton');
    if (!skeleton || skeleton.getAttribute('data-bc-preview')) return;

    var thumb = null;
    this.thumbs.forEach(function (candidate) {
      if (candidate.index === photo.index) thumb = candidate;
    });
    var img = thumb && thumb.link.querySelector('img');
    if (!img || !img.complete || !img.naturalWidth) return;

    skeleton.style.backgroundImage = 'url("' + (img.currentSrc || img.src) + '")';
    skeleton.setAttribute('data-bc-preview', 'true');
  };

  /* The skeleton belongs to whatever is wanted right now, so a tap that lands
     during another photo's download takes the skeleton with it. */
  Gallery.prototype.paintState = function () {
    var wanted = this.wanted;
    this.photos.forEach(function (photo, i) {
      photo.wrapper.classList.toggle(CLASSES.loading, i === wanted && photo.state !== 'ready' && photo.state !== 'failed');
    });
  };

  Gallery.prototype.syncThumbs = function () {
    var wanted = this.wanted;
    this.thumbs.forEach(function (thumb) {
      var active = thumb.index === wanted;
      thumb.link.classList.toggle(CLASSES.activeThumb, active);
      if (active) {
        thumb.link.setAttribute('aria-current', 'true');
      } else {
        thumb.link.removeAttribute('aria-current');
      }
    });
  };

  /* One photo at a time, nearest first, and never while something is already
     in flight - a preload must not sit in front of the photo being waited on. */
  Gallery.prototype.topUp = function () {
    var self = this;
    var reach = lookAhead();
    if (!reach) return;

    var busy = false;
    this.photos.forEach(function (photo) {
      if (photo.state === 'loading') busy = true;
    });
    /* Something is already on the wire - most often the featured photo, right
       after the page is parsed. Come back for the neighbours once it is done,
       rather than dropping the queue on the floor until the next tap. */
    if (busy) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = window.setTimeout(function () {
        self.topUp();
      }, 400);
      return;
    }

    var wanted = this.wanted;
    var target = null;
    var nearest = Infinity;
    this.photos.forEach(function (photo, i) {
      var distance = Math.abs(i - wanted);
      if (photo.state === 'idle' && distance <= reach && distance < nearest) {
        nearest = distance;
        target = photo;
      }
    });
    if (!target) return;

    this.load(target, 'low').then(function () {
      self.topUpSoon();
    });
  };

  Gallery.prototype.topUpSoon = function () {
    var self = this;
    whenIdle(function () {
      self.topUp();
    });
  };

  Gallery.prototype.bind = function () {
    var self = this;
    var list = document.querySelector(SELECTORS.thumbList);

    if (list) {
      /* Capture phase, and the event is stopped here: theme.js binds its own
         click on every thumbnail and would reveal the next photo before it has
         decoded, which is the stale frame this file exists to remove. */
      list.addEventListener('click', function (event) {
        var node = event.target;
        while (node && node !== list && !(node.classList && node.classList.contains('product-single__thumbnail'))) {
          node = node.parentNode;
        }
        if (!node || node === list) return;

        var index = -1;
        self.thumbs.forEach(function (thumb) {
          if (thumb.link === node) index = thumb.index;
        });
        if (index < 0) return;

        event.preventDefault();
        event.stopPropagation();
        self.show(index);
      }, true);
    }

    /* Variant switching calls theme.js `_switchImage` directly and never goes
       through a thumbnail, so the class itself is watched. Anything revealed
       that is not what this file last wanted came from outside and is adopted. */
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var wrapper = records[i].target;
        if (wrapper.classList.contains(CLASSES.hidden)) continue;
        var index = self.wrappers.indexOf(wrapper);
        if (index > -1 && index !== self.wanted) {
          self.show(index);
          return;
        }
      }
    });
    this.wrappers.forEach(function (wrapper) {
      observer.observe(wrapper, { attributes: true, attributeFilter: ['class'] });
    });

    /* A photo's `sizes` is measured at the moment it is requested, so a rotation
       needs nothing for the ones still queued. This only nudges the queue back
       into life, since a rotation is also when a stalled top-up tends to sit. */
    window.addEventListener('resize', function () {
      window.clearTimeout(self.resizeTimer);
      self.resizeTimer = window.setTimeout(function () {
        self.topUpSoon();
      }, 250);
    });
  };

  function init() {
    var roots = document.querySelectorAll(SELECTORS.root);
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].getAttribute('data-bc-product-gallery-bound')) continue;
      roots[i].setAttribute('data-bc-product-gallery-bound', 'true');
      if (supported()) {
        new Gallery(roots[i]);
      } else {
        loadEverythingEagerly(roots[i]);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
